import React, { useEffect, useState } from 'react';
import './style.scss';
import {
  AgreeDocumentMutationVariables,
  Exact,
  GetAgreementDocumentQuery,
  useAgreeDocumentMutation,
} from 'views/utils/graphql/graphql';
import { InputConsent } from 'views/components/molecules/establishes/inputConsent';
import { Button, ButtonSize, ButtonType, FillType } from 'views/components/atoms/button';
import { UseQueryState } from 'urql';
import useOnceSubmit from 'views/utils/useOnceSubmit';
import { useGlobalToastHandler } from 'views/components/organisms/globalToast/hooks';

enum AgreementDocumentType {
  termsAndConditions = 1,
  preContract = 2,
  infoSharing = 3,
  antisocial = 4,
}

type AgreementDocumentProps = {
  agreementDocument: UseQueryState<
    GetAgreementDocumentQuery,
    Exact<{
      [key: string]: never;
    }>
  >;
  onRequestClose?: () => void;
};

export const AgreementDocument = (props: AgreementDocumentProps) => {
  const { onRequestClose, agreementDocument } = props;

  const [agreeData, agree] = useAgreeDocumentMutation();
  const { showError } = useGlobalToastHandler();

  const initialCheckList = Object.entries(AgreementDocumentType).map(([_, v]) => {
    return {
      type: v as AgreementDocumentType,
      checked: false,
    };
  });
  const [checkList, setCheckList] = useState(initialCheckList);

  const [agreeTypes, setAgreeTypes] = useState<number[]>([]);

  const agreeSubmit = useOnceSubmit<AgreeDocumentMutationVariables>(
    {
      onSubmit: async (variables) => {
        const res = await agree(variables);
        if (res.data?.AgreeDocument?.Code === 0) {
          onRequestClose && onRequestClose();
        } else if (res.data) {
          showError(res.data.AgreeDocument?.Message);
        }
      },
      isLoadingForceCenter: true,
    },
    [],
  );

  useEffect(() => {
    if (agreeTypes.length === 0) return;
    const variables: AgreeDocumentMutationVariables = {
      input: {
        Types: agreeTypes,
      },
    };
    agreeSubmit(variables);
  }, [agreeTypes]);

  if (agreementDocument.fetching) return;
  if (!agreementDocument.data?.GetAgreementDocument?.Data) return;

  const aggreementDocumentInfoList = [
    {
      type: AgreementDocumentType.termsAndConditions,
      title: '証券取引約款についての同意',
      checkText: '変更後の「証券取引約款」が適用されることに同意します',
      linkTitle: '証券取引約款について',
    },
    {
      type: AgreementDocumentType.preContract,
      title: '契約締結前交付書面についての同意',
      checkText: '変更後の「金銭・有価証券等の預託および記帳に関する契約締結前交付書面」の内容を理解の上、同意します',
      linkTitle: '契約締結前交付書面について',
    },
    {
      type: AgreementDocumentType.infoSharing,
      title: '情報共有についての同意',
      checkText: '変更後の「情報の相互提供・共有に関する同意書」の内容を理解の上、同意します',
      linkTitle: '情報の相互提供・共有に関する同意書について',
    },
    {
      type: AgreementDocumentType.antisocial,
      title: '反社会的勢力ではないことの同意',
      checkText: '変更後の「反社会的勢力ではないことの表明・確約に関する同意」の内容を理解の上、同意します',
      linkTitle: '反社会的勢力ではないことの表明・確約について',
    },
  ];

  // 未読のものだけを抽出して表示用に加工する
  const targetData = agreementDocument.data.GetAgreementDocument.Data.filter((d) => !d?.Read).map((d) =>
    Object.assign(
      {},
      d,
      aggreementDocumentInfoList.find((dt) => dt.type === d?.Type),
    ),
  );

  const handleAgreeCheck = () => {
    setAgreeTypes(checkList.filter((d) => d.checked).map((d) => d.type));
  };

  const disableAgreeButton = () => {
    return checkList.some((d) => targetData.find((x) => x.type === d.type) && !d.checked);
  };

  return (
    <div className="agreementWrapper">
      <div className="agreementWrapper_title">規約内容変更事項についての再同意</div>
      {targetData.map((d) =>
        d ? (
          <InputConsent
            title={d.title}
            name={AgreementDocumentType[d.Type]}
            checkText={d.checkText}
            linkTitle={d.title}
            linkUrl={d?.URL}
            batchDangerWord="必須"
            isChecked={checkList.find((cl) => cl.type === d.type)?.checked ?? false}
            setIsChecked={(e) => {
              setCheckList((prev) =>
                prev.map((x) => (x.type === d.type ? { checked: e.target.checked, type: d.type } : x)),
              );
            }}
            isEnable={false}
          />
        ) : null,
      )}
      <Button
        label="同意する"
        type={ButtonType.Button}
        fillType={disableAgreeButton() ? FillType.primaryEmpty : FillType.primaryFill}
        buttonSize={ButtonSize.sizeM}
        onClick={() => {
          if (disableAgreeButton()) return;
          handleAgreeCheck();
        }}
      />
    </div>
  );
};
